import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

const DEFAULT_PPM = 420;
const MIN_PPM = 400;
const MAX_PPM = 2000;

/**
 * Admin-only forced CO2 recalibration for the SEN66. Sends the target
 * reference concentration over serial so the sensor pins its CO2 baseline to a
 * known value. This is destructive if done in bad air, so it requires an
 * in-range reference ppm and an explicit warning acknowledgement.
 */
export default function CalibrateCo2Card({ serialClient, onError = undefined }) {
  const [ppm, setPpm] = useState(String(DEFAULT_PPM));
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const numericPpm = Number(ppm);
  const outOfRange =
    ppm === '' ||
    Number.isNaN(numericPpm) ||
    numericPpm < MIN_PPM ||
    numericPpm > MAX_PPM;

  const handleCalibrate = useCallback(async () => {
    setBusy(true);
    setSuccess(false);
    setError(null);
    try {
      await serialClient.calibrateCo2(numericPpm);
      setSuccess(true);
    } catch (err) {
      const message = err.message || 'Calibration failed';
      setError(message);
      if (onError) onError(message);
    } finally {
      setBusy(false);
    }
  }, [serialClient, onError, numericPpm]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Calibrate CO₂ (admin)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Force the SEN66 CO₂ baseline to a known reference concentration.
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          Only run this outdoors in fresh air (~420 ppm). A wrong value
          miscalibrates the sensor.
        </Alert>

        <TextField
          label="Reference CO₂ (ppm)"
          type="number"
          value={ppm}
          onChange={(e) => setPpm(e.target.value)}
          disabled={busy}
          error={outOfRange}
          helperText={
            outOfRange ? `Enter a value between ${MIN_PPM} and ${MAX_PPM} ppm.` : ' '
          }
          inputProps={{ min: MIN_PPM, max: MAX_PPM }}
          sx={{ mb: 2, display: 'block' }}
        />

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <TuneIcon />}
          onClick={handleCalibrate}
          disabled={busy || outOfRange}
        >
          {busy ? 'Calibrating...' : 'Calibrate CO₂'}
        </Button>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            CO₂ calibration complete.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

CalibrateCo2Card.propTypes = {
  serialClient: PropTypes.object.isRequired,
  onError: PropTypes.func,
};
